import jsPDF from "jspdf";

export function generateProposalPDF() {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const navy = [10, 25, 47];
  const red = [139, 0, 0];
  const slate = [51, 65, 85];
  const muted = [100, 116, 139];
  const gold = [184, 134, 11];
  const cream = [249, 247, 241];
  const margin = 64;
  let y = margin;
  let pageNum = 0;

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - margin * 2;

  // ----- Vector logo: bridge → tunnel → sunlight -----
  const drawBridgeLogo = (x, y0, scale = 1, color = navy, accent = gold) => {
    const w = 70 * scale;
    const h = 50 * scale;
    // sun rays
    doc.setDrawColor(...accent);
    doc.setLineWidth(0.8 * scale);
    const cx = x + w * 0.78;
    const cy = y0 + h * 0.55;
    [[-1, 0], [1, 0], [0, -1], [-0.7, -0.7], [0.7, -0.7]].forEach(([dx, dy]) => {
      doc.line(cx + dx * 7 * scale, cy + dy * 7 * scale, cx + dx * 11 * scale, cy + dy * 11 * scale);
    });
    // tunnel hill
    doc.setFillColor(...color);
    const hillW = 18 * scale;
    doc.roundedRect(x + w * 0.6, y0 + h * 0.35, hillW + 6 * scale, h * 0.55, 8 * scale, 8 * scale, "F");
    // tunnel opening (gold)
    doc.setFillColor(...accent);
    doc.roundedRect(x + w * 0.66, y0 + h * 0.5, 12 * scale, h * 0.4, 4 * scale, 4 * scale, "F");
    // sun visible through tunnel
    doc.setFillColor(244, 215, 122);
    doc.circle(x + w * 0.72, y0 + h * 0.78, 2.4 * scale, "F");
    // bridge deck
    doc.setDrawColor(...color);
    doc.setLineWidth(1.5 * scale);
    doc.line(x, y0 + h * 0.78, x + w * 0.66, y0 + h * 0.78);
    // suspension cable arch
    doc.setLineWidth(0.8 * scale);
    const arch = (t) => {
      const ax = x + t * w * 0.66;
      const ay = y0 + h * 0.78 - Math.sin(t * Math.PI) * h * 0.42;
      return [ax, ay];
    };
    let prev = arch(0);
    for (let i = 1; i <= 24; i++) {
      const cur = arch(i / 24);
      doc.line(prev[0], prev[1], cur[0], cur[1]);
      prev = cur;
    }
    // piers
    doc.setLineWidth(1.4 * scale);
    doc.line(x + w * 0.2, y0 + h * 0.78, x + w * 0.2, y0 + h * 0.92);
    doc.line(x + w * 0.5, y0 + h * 0.78, x + w * 0.5, y0 + h * 0.92);
  };

  const drawFooter = () => {
    if (pageNum === 0) return;
    const fy = pageH - 28;
    doc.setDrawColor(...gold);
    doc.setLineWidth(0.4);
    doc.line(margin, fy - 10, pageW - margin, fy - 10);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...gold);
    doc.text("THE BRIDGE MODEL  \u00B7  PROPOSAL 01  \u00B7  THE BAYTOWN BRIDGE  \u00B7  PROPOSED \u2014 PENDING COUNCIL APPROVAL", margin, fy);
    doc.setTextColor(...muted);
    doc.text(`thebridgemodel.org  \u00B7  Page ${pageNum}`, pageW - margin, fy, { align: "right" });
  };

  const newPage = () => {
    drawFooter();
    doc.addPage();
    pageNum += 1;
    y = margin + 8;
  };

  const addPageIfNeeded = (needed = 30) => {
    if (y + needed > pageH - margin - 18) {
      newPage();
    }
  };

  const heading = (text, size = 22) => {
    // Space-before
    y += size > 18 ? 20 : 16;
    addPageIfNeeded(size + 40);
    doc.setFont("times", "bold");
    doc.setFontSize(size);
    doc.setTextColor(...navy);
    doc.text(text, margin, y);
    y += 6;
    doc.setDrawColor(...red);
    doc.setLineWidth(1.6);
    doc.line(margin, y, margin + 56, y);
    y += 22;
  };

  const subheading = (text) => {
    y += 12; // space-before
    addPageIfNeeded(36);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...red);
    doc.text("\u2014 \u2014", margin, y);
    y += 4;
    doc.setFont("times", "bold");
    doc.setFontSize(13.5);
    doc.setTextColor(...navy);
    doc.text(text, margin, y + 10);
    y += 22;
  };

  const para = (text, opts = {}) => {
    const sz = opts.size || 10.5;
    const lh = opts.lineHeight || 16;
    doc.setFont(opts.font || "helvetica", opts.style || "normal");
    doc.setFontSize(sz);
    doc.setTextColor(...(opts.color || slate));
    const lines = doc.splitTextToSize(text, contentW);
    lines.forEach((line) => {
      addPageIfNeeded(lh + 2);
      doc.text(line, margin, y);
      y += lh;
    });
    y += opts.gap ?? 10; // paragraph spacing
  };

  const bullet = (text) => {
    const lh = 16;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.5);
    doc.setTextColor(...slate);
    const lines = doc.splitTextToSize(text, contentW - 18);
    lines.forEach((line, i) => {
      addPageIfNeeded(lh + 2);
      if (i === 0) {
        doc.setFillColor(...red);
        doc.rect(margin + 1, y - 7, 4, 4, "F");
      }
      doc.text(line, margin + 16, y);
      y += lh;
    });
    y += 4;
  };

  // ----- Stat-row card (3-column hero numbers) -----
  const statRow = (cells) => {
    addPageIfNeeded(82);
    const gap = 8;
    const cellW = (contentW - gap * (cells.length - 1)) / cells.length;
    cells.forEach((c, i) => {
      const x = margin + i * (cellW + gap);
      doc.setFillColor(...navy);
      doc.rect(x, y, cellW, 70, "F");
      // gold top rule
      doc.setFillColor(...gold);
      doc.rect(x, y, cellW, 2, "F");
      // value
      doc.setFont("times", "bold");
      doc.setFontSize(22);
      doc.setTextColor(...cream);
      doc.text(c.value, x + 14, y + 32);
      // label
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(212, 175, 55);
      const labelLines = doc.splitTextToSize(c.label.toUpperCase(), cellW - 28);
      let ly = y + 48;
      labelLines.forEach((l) => {
        doc.text(l, x + 14, ly);
        ly += 10;
      });
    });
    y += 70 + 16;
  };

  // ----- Numbered phase box (for implementation strategy) -----
  const phaseBox = (n, period, title, body) => {
    addPageIfNeeded(90);
    const lines = doc.splitTextToSize(body, contentW - 64);
    const h = Math.max(82, 40 + lines.length * 14);
    // navy left tab with number
    doc.setFillColor(...navy);
    doc.rect(margin, y, 48, h, "F");
    doc.setFont("times", "bold");
    doc.setFontSize(24);
    doc.setTextColor(...gold);
    doc.text(String(n).padStart(2, "0"), margin + 24, y + 36, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(212, 175, 55);
    doc.text("PHASE", margin + 24, y + 50, { align: "center" });
    // body
    doc.setFillColor(...cream);
    doc.rect(margin + 48, y, contentW - 48, h, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...red);
    doc.text(period.toUpperCase(), margin + 60, y + 16);
    doc.setFont("times", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...navy);
    doc.text(title, margin + 60, y + 32);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...slate);
    let by = y + 48;
    lines.forEach((l) => {
      doc.text(l, margin + 60, by);
      by += 14;
    });
    y += h + 10;
  };

  const pullQuote = (quoteText, attribution) => {
    const inset = 32;
    const innerW = contentW - inset * 2;
    doc.setFont("times", "italic");
    doc.setFontSize(15);
    const lines = doc.splitTextToSize(`\u201C${quoteText}\u201D`, innerW);
    const blockH = lines.length * 22 + 72;
    addPageIfNeeded(blockH);

    doc.setFillColor(...cream);
    doc.rect(margin, y, contentW, blockH, "F");
    doc.setDrawColor(...red);
    doc.setLineWidth(3);
    doc.line(margin, y, margin, y + blockH);

    doc.setTextColor(...navy);
    let qy = y + 36;
    lines.forEach((line) => {
      doc.text(line, margin + inset, qy);
      qy += 22;
    });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...red);
    doc.text(attribution, margin + inset, qy + 14);

    y += blockH + 18;
  };

  // ----- Cover -----
  pageNum = 0; // cover doesn't show footer
  doc.setFillColor(...navy);
  doc.rect(0, 0, pageW, pageH, "F");

  // Logo top-left
  drawBridgeLogo(margin, margin - 6, 1.6, [249, 247, 241], [212, 175, 55]);

  doc.setTextColor(212, 175, 55);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("THE BRIDGE MODEL  \u00B7  PROPOSAL 01  \u00B7  A MUNICIPAL PROPOSAL", margin, 160);

  // PROPOSED status pill — pending approval
  doc.setDrawColor(212, 175, 55);
  doc.setFillColor(212, 175, 55, 0.12);
  doc.setLineWidth(0.5);
  doc.roundedRect(margin, 168, 320, 18, 2, 2);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(212, 175, 55);
  doc.text("\u25CF  PROPOSED  \u00B7  PENDING COUNCIL APPROVAL  \u00B7  NOT YET ADOPTED", margin + 10, 181);

  doc.setTextColor(249, 247, 241);
  doc.setFont("times", "bold");
  doc.setFontSize(44);
  doc.text("The Baytown Bridge", margin, 234);
  doc.setFontSize(18);
  doc.setFont("times", "italic");
  doc.text("A Multigenerational Service Connector Initiative", margin, 268);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(212, 175, 55);
  doc.text("Proposal 01 of The Bridge Model  |  thebridgemodel.org", margin, 290);

  // SAM.gov badge
  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(0.6);
  doc.roundedRect(margin, 306, 280, 26, 3, 3);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(212, 175, 55);
  doc.text("\u2714  SAM.gov  \u00B7  ACTIVE FEDERAL REGISTRATION", margin + 10, 322);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(203, 213, 225);
  doc.setFontSize(7);
  doc.text("STERILE MATE LLC  \u00B7  CAGE 9GH66  \u00B7  ALL AWARDS  \u00B7  GRANTS & CONTRACTS", margin + 10, 332);

  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(1);
  doc.line(margin, 356, margin + 80, 356);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(203, 213, 225);
  const coverLead = doc.splitTextToSize(
    "Baytown has the gears \u2014 Lee College, Harris Health, the Baytown Housing Authority, Workforce Solutions, GCCISD. What is missing is the chain. The Baytown Bridge supplies that chain: a high-touch service connector model that walks single-mother households and aging-in-place seniors through the full ecosystem until they reach a thriving baseline.",
    contentW
  );
  let coverY = 388;
  coverLead.forEach((l) => {
    doc.text(l, margin, coverY);
    coverY += 17;
  });

  // Hero numbers strip on cover
  const sx = margin;
  const sw = (contentW - 16) / 4;
  const heroNums = [
    { v: "1,500", l: "Households per year" },
    { v: "$2,756", l: "Per household / yr" },
    { v: "1:38", l: "Caseload cap" },
    { v: "5-yr", l: "Contract horizon" },
  ];
  heroNums.forEach((h, i) => {
    const hx = sx + i * (sw + 5.3);
    doc.setDrawColor(212, 175, 55);
    doc.setLineWidth(0.4);
    doc.rect(hx, pageH - 240, sw, 70);
    doc.setFont("times", "bold");
    doc.setFontSize(20);
    doc.setTextColor(212, 175, 55);
    doc.text(h.v, hx + 12, pageH - 210);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(203, 213, 225);
    doc.text(h.l.toUpperCase(), hx + 12, pageH - 184);
  });

  doc.setTextColor(249, 247, 241);
  doc.setFont("times", "bold");
  doc.setFontSize(13);
  doc.text("George Broughton III", margin, pageH - 130);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(203, 213, 225);
  doc.text("Proprietor & Program Designer, The Bridge Model", margin, pageH - 114);
  doc.text("Founder & CEO, Sterile Mate LLC", margin, pageH - 100);
  doc.text("Trained inside The Woodlawn Organization / WCDC, Chicago (2001\u20132007)", margin, pageH - 86);
  doc.text("george.broughton@sterilemate.com", margin, pageH - 72);

  // ----- Content pages -----
  newPage();
  doc.setTextColor(...navy);

  // ---------- Foreword · The Bridge Model Story ----------
  heading("Foreword \u00B7 The Bridge Model Story", 24);
  para(
    "How a 65-year-old Chicago community-service tradition becomes Baytown's blueprint \u2014 and the foundation of a new golden age of municipal-service work across the Gulf Coast."
  );

  subheading("Movement 01 \u00B7 The Lineage (1960\u20132000)");
  para(
    "The story does not begin in Texas. It begins in 1960, in Woodlawn, on the South Side of Chicago, with the founding of The Woodlawn Organization (TWO) \u2014 one of the most influential Black-led community organizations in American history. TWO was Chicago's pillar community-outreach institution: a coalition of churches, block clubs, and resident leaders that put the work of stabilizing families directly into the hands of the community itself."
  );
  para(
    "By the 1990s, TWO had stood up its housing-development arm \u2014 the Woodlawn Community Development Corporation (WCDC) \u2014 and was working hand-in-glove with the Chicago Housing Authority (CHA) to operate the very public-housing properties their residents lived in. The Service Connector framework \u2014 one trained caseworker, multi-year relationship, every service line \u2014 was forged inside that work. It became a federally-recognized blueprint, cited by HUD, replicated nationally, and tested over decades on some of the most distressed housing portfolios in the country."
  );
  para(
    "Anchoring all of it was Dr. Leon Finney Jr. \u2014 Chairman of TWO, Pastor of Metropolitan Apostolic Community Church, and one of the great living examples of what it looks like when civil-rights organizing, faith-based community outreach, and large-scale housing operations are run by the same set of hands."
  );

  subheading("Movement 02 \u00B7 The Formation (1971\u20132000)");
  para(
    "In George Broughton III's own words: \"I am a product of the inner city. A broken home. Separated from my mother at age nine. I left home at fifteen and I had to grow up fast. I have been working since I was fourteen.\""
  );
  para(
    "\"College was not an option I could see. But I knew I did not want to veer down a road of drugs and crime. So I worked. Through some really dark times I knew I wanted to make an impact, and I looked in many dark places looking for love, peace, comfort, security, and how to become a man.\""
  );
  para(
    "\"Thankfully, I landed in the presence of Dr. Leon Finney in 2000, at the age of twenty-nine. That is where this work begins for me.\""
  );

  subheading("Movement 03 \u00B7 The Apprenticeship (2000\u20132007)");
  para(
    "In 2000, George Broughton III joined Metropolitan Apostolic Community Church under the pastorship of Dr. Leon Finney Jr. The church relationship came first; the professional and personal relationships grew from there. Dr. Finney became George's pastor, his employer, his mentor, and ultimately his godfather \u2014 the formative voice on leadership, on community, and on the discipline of being in service."
  );
  para(
    "From April 2001 to May 2007, George worked under both TWO and WCDC simultaneously \u2014 as a Case Manager Service Connector responsible for a continuous 105-family caseload on CHA properties, and as a Community Outreach Leader running the church- and block-level organizing that fed the case-management pipeline. Six years. Two roles. One mentor."
  );
  para(
    "What he learned was not a curriculum. It was a posture. You don't manage families from behind a desk. You don't measure success by enrollments. You measure it by who is still standing, still housed, still working, still in school five years after you first met them. And you do the work because you were sent \u2014 by the community, by the church, by the people who trained you."
  );

  subheading("The Katrina Moment \u00B7 2005");
  para(
    "When Hurricane Katrina struck the Gulf Coast in 2005, TWO and WCDC stood up emergency housing for the transients and refugees who arrived in Chicago. The work was overwhelming. Many in the planning room were weary \u2014 uncertain whether the effort could meaningfully matter to people who had lost everything. George took those doubts to Dr. Finney privately. What Dr. Finney said in response became the operating philosophy of everything George has built since:"
  );

  pullQuote(
    "Whenever you encounter tragedy in our communities, instead of complaining, always find a way to be in service of others, and you will always win.",
    "DR. LEON FINNEY JR.  \u00B7  CHAIRMAN, THE WOODLAWN ORGANIZATION  \u00B7  PASTOR, METROPOLITAN APOSTOLIC COMMUNITY CHURCH"
  );

  subheading("Movement 04 \u00B7 The Inheritance (2026 and beyond)");
  para(
    "Dr. Finney passed in 2020. The tradition he carried \u2014 Alinsky-era community organizing fused with faith-rooted pastoral leadership and large-scale housing operations \u2014 is now in the hands of the practitioners he personally trained. There aren't many of us. The model has to be rebuilt, formalized, and made replicable, or it disappears with the generation that built it. This is the right time to launch a fresh version of this program. The convergence below is rare \u2014 it is the launch window for a decade-long expansion that can carry Gulf Coast communities into their golden age:"
  );
  bullet("The post-COVID social-services collapse. Every American municipality is staring at the same fragmentation Woodlawn faced sixty years ago \u2014 only now it spans senior care, mental health, workforce, housing, and childcare in parallel. The case for a single trained connector per family has never been stronger.");
  bullet("A generational handoff is happening. The first generation of civil-rights-era community organizers is passing on. The methods they built \u2014 high-touch, faith-rooted, multi-generational case management \u2014 have to be carried forward by practitioners they personally trained.");
  bullet("Texas is the right ground. Baytown, La Porte, Pasadena, Texas City \u2014 the Gulf Coast LMI corridor \u2014 combine the same demographic pressure Chicago had in 1960 with one critical difference: living-wage industrial and medical jobs are already here. The Bridge Model doesn't need to create the opportunity. It just needs to walk families to it.");
  bullet("Federal CDBG dollars are flowing now. The City of Baytown's 2025\u20132029 Consolidated Plan is open. Harris County matches are available. This is the funding window in which a properly-built, fidelity-audited service-connector program can be stood up at scale \u2014 and replicated city by city through the rest of the decade.");

  para(
    "Baytown Bridge \u2014 Proposal 01 of The Bridge Model \u2014 is not a proposal for a new social-services experiment. It is the continuation of a sixty-five-year tradition, carried forward by someone who was trained inside it, scaled for the city that needs it next."
  );

  // In Memoriam
  y += 14;
  addPageIfNeeded(110);
  doc.setDrawColor(...gold);
  doc.setLineWidth(0.6);
  doc.line(margin + 80, y, pageW - margin - 80, y);
  y += 14;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...gold);
  doc.text("IN MEMORIAM", pageW / 2, y, { align: "center" });
  y += 18;
  doc.setFont("times", "italic");
  doc.setFontSize(11);
  doc.setTextColor(...slate);
  doc.text("This work is dedicated to the memory of my pastor, my mentor, and my godfather.", pageW / 2, y, { align: "center" });
  y += 22;
  doc.setFont("times", "bold");
  doc.setFontSize(15);
  doc.setTextColor(...navy);
  doc.text("Dr. Leon Finney Jr.", pageW / 2, y, { align: "center" });
  y += 18;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...red);
  doc.text("1938  \u2014  2020", pageW / 2, y, { align: "center" });
  y += 14;
  doc.setTextColor(...slate);
  doc.text("Chairman, The Woodlawn Organization  \u00B7  Pastor, Metropolitan Apostolic Community Church", pageW / 2, y, { align: "center" });
  y += 22;
  doc.setDrawColor(...gold);
  doc.setLineWidth(0.6);
  doc.line(margin + 80, y, pageW - margin - 80, y);
  y += 22;

  subheading("Equity & Access Commitment");
  para(
    "The Bridge Model serves underserved households of every ethnic and social background \u2014 Hispanic/Latino, Black, White, Asian, immigrant and refugee, urban and rural \u2014 without prejudice and without precondition. Service is not contingent on documentation status, voucher status, religion, gender identity, sexual orientation, criminal record, family composition, or political belief. The only criterion is need."
  );
  bullet("Civil-rights compliance: Title VI of the Civil Rights Act, Fair Housing Act, Americans with Disabilities Act, Section 504, Age Discrimination Act.");
  bullet("Bilingual by default \u2014 site, intake, and case-management protocols delivered in English and Spanish from day one; additional language access (Vietnamese, Arabic, Haitian Creole, others) added on demand.");
  bullet("Faith-rooted, never faith-required. The Bridge Model is built on a tradition of faith-rooted community service, but participation never requires religious affiliation or practice.");
  bullet("ADA-compliant intake locations, mobility-accommodating home visits, large-print materials for senior clients, and disability-rights coordination with Harris Health and Texas DARS built into every Service Connector's caseload.");
  bullet("The independent annual evaluation publishes service distribution by ethnicity, language, age, and household type. If any group is underserved, the agency in question loses its contract.");

  heading("Executive Summary");
  para(
    "Every day, single mothers and seniors in Baytown fall through the cracks of a fragmented social service system \u2014 not because local resources don't exist, but because navigating them requires an air traffic controller. The Baytown Bridge proposes that air traffic controller: a permanent, high-touch service connector agency that holds long-term relationships with Lee College, Harris Health, the Baytown Housing Authority, Workforce Solutions, Meals on Wheels, and Harris County Transit. We accept residents regardless of whether they hold a federal housing voucher, and we walk them through education, housing, employment, and healthcare until they reach a thriving baseline."
  );
  statRow([
    { value: "1,500", label: "Households / yr" },
    { value: "$4.65M", label: "Yr-1 all-in" },
    { value: "$2,756", label: "Per HH / yr" },
    { value: "1:38", label: "Caseload cap" },
  ]);

  heading("The Local Context");
  para(
    "Approximate Baytown indicators (drawn from U.S. Census ACS 5-year, HUD, TEA TAPR, and FBI UCR reporting): median household income of $59,910, a poverty rate of 18.2%, child poverty of 24.5%, an uninsured rate of 19.1%, and a high-school completion rate of 81.4%. GCCISD lags state averages on reading (67% vs. 74%) and math (62% vs. 70%) at the Approaches grade level. Baytown is majority-minority — 47.2% Hispanic/Latino, 31.0% White, 17.4% Black, 2.1% Asian. Property and violent crime have trended downward since 2018, but stability gaps remain concentrated in single-mother households and aging-in-place seniors."
  );

  heading("The Two-Pronged Strategy");
  subheading("Senior Track — Preserving Dignity and Independence");
  bullet("Health & Safety — chronic disease management, prescription assistance, home safety assessments to prevent falls.");
  bullet("Financial Protection — shielding seniors from predatory scams, maximizing SNAP, and securing LIHEAP utility assistance.");
  bullet("Social Connection — bridging the digital divide with basic tech training, and pairing seniors with community centers to fight isolation.");

  subheading("Whole-Family Track — Building Generational Wealth and Stability");
  bullet("Employment & Education — direct enrollment into Lee College high-demand certificates (medical billing, logistics, welding) paired with Workforce Solutions childcare subsidies.");
  bullet("Housing Security — transitioning families from week-to-week rentals or overcrowded spaces into stable leases or first-time homeownership programs.");
  bullet("Child Development — enrolling children in Head Start and after-school programs so parents can work or attend training stress-free.");

  heading("The Trade Pipeline — From At-Risk to Homeowner");
  para(
    "Baytown's highest-leverage hiring economy is not a 4-year degree — it is the skilled trade pipeline. ExxonMobil's Baytown Complex employs 8,500+ workers and actively hires electricians, pipe fitters, iron workers, carpenters, and craft labor. Houston Methodist Baytown Hospital has 60+ active openings and is in active campus expansion. Indeed indexes roughly 937 HVAC openings in the Baytown area. The Dallas Fed's 2026 Q1 report names construction and health services as Texas's #1 and #2 growth industries in 2025."
  );
  subheading("Six trade tracks Bridge actively recruits into");
  bullet("HVAC Technician — Lee College certificate, 12–18 months. Starting $54,371–$57,144/yr; journeyman $65k–$85k.");
  bullet("Welding / Pipefitting — Lee College certificate, 9–15 months. ~$25.85/hr starting; 6G specialty $70k–$100k.");
  bullet("Process Technology — Lee College AAS (2 yr), pre-recruited by ExxonMobil, Covestro, Chevron Phillips. $60k–$110k.");
  bullet("Commercial Driver (CDL) — Workforce Solutions partner training, 4–8 weeks. $50k–$95k with tanker/hazmat endorsements.");
  bullet("IT / Help Desk → Cybersecurity — Lee College + stackable industry certs, 6–12 months. $45k–$110k specialist.");
  bullet("Hospital Skilled Support — Lee College Allied Health certificates, 6–24 months. $42k–$90k surgical tech / sonography.");
  subheading("The 5-Year Ladder to Homeownership");
  bullet("Year 0 (Intake): under $25,000 — unstable gig work, no benefits.");
  bullet("Year 1: $35k–$42k — enrolled in Lee College or CDL training, working as helper/apprentice.");
  bullet("Year 2: $54k–$65k — credential complete, first stable W-2 with benefits.");
  bullet("Year 3–4: $70k–$90k — journeyman or specialist; mortgage-qualifying income.");
  bullet("Year 5+: $85k+ — pre-qualified for first-time homeownership via Baytown Housing Authority and HUD partner programs. Generational wealth begins here.");
  para(
    "Trade school vs. 4-year college (for the families Bridge serves): trade certificates produce a $45k–$60k year-one income in 6–24 months at $2,000–$8,000 of tuition, with apprentice/helper income paid during training. The default 4-year degree path takes 4–6 years, costs $40k–$120k, leaves the average graduate with $30k+ in debt, and produces $0 of income during studies. Bridge does not steer anyone away from college — we steer everyone toward the credential that produces stable, mortgage-qualifying income fastest, in the city they actually live in. Connecting at-risk men to these trades is the single most reliable lever for whole-family stability, public safety, and homeownership."
  );

  heading("The Senior Care Pipeline — Aging in Place");
  para(
    "The senior track is not a separate program — it is the same Bridge connector working a second cohort under the same roof. Most Baytown low-income households are multigenerational; the same mother working through Lee College often has a parent under her roof whose missed Medicare appointment or unsafe shower becomes next year's nursing-home placement. The destination changes: instead of a mortgage, the goal is aging safely at home with dignity, daily support, and a phone that still rings on Sunday."
  );
  subheading("Verified Senior-Service Partners");
  bullet("Houston-Harris County Area Agency on Aging (AAA, 1-800-437-7396): benefits counseling, nutrition, in-home supports, caregiver help for adults 60+.");
  bullet("Harris County RIDES (rides.harriscountytx.gov): subsidized transit for seniors at or below 200% FPL — the difference between a kept cardiologist appointment and a missed one.");
  bullet("Meals on Wheels / Interfaith Ministries / YWCA Houston: home-delivered meals 7 days a week + congregate meals at neighborhood senior centers.");
  bullet("Harris Health + Medicare Home Health: part-time skilled nursing, PT/OT, and home health aide visits when the senior is properly enrolled.");

  subheading("Six Senior Service Pillars");
  bullet("Housing & Home Modification — grab bars, ramps, lighting, smoke + CO detectors. Falls are the #1 cause of nursing-home placement. Bridge prevents the fall.");
  bullet("In-Home Care — Medicare home-health navigation, personal-care attendants via AAA, and respite for overstretched family caregivers.");
  bullet("Health Navigation — Medicare/Medicaid dual-eligible enrollment, primary care via Harris Health, chronic disease management, Rx affordability, telehealth setup.");
  bullet("Benefits & Resources — SNAP, LIHEAP, Texas Over-65 property tax freeze, Medicare Savings Program, Rx Extra Help, VA screening. Average enrolled senior recovers $3,000–$6,000/yr in benefits already on the books.");
  bullet("Social Connection & Programs — weekly senior-center attendance, faith-community pairings, tech training, scam protection, Senior Companion phone check-ins. Isolation is treated as the clinical risk it is.");
  bullet("Encore Employment & Purpose — SCSEP placements, AAA part-time work, RSVP volunteer matches, and remote help-desk pathways for seniors who want or need supplemental income.");

  subheading("The 12-Month Aging-in-Place Ladder");
  bullet("Day 0 (Crisis): unsafe home, missed medications, isolated, stairs they cannot climb.");
  bullet("30 Days (Stabilized): Medicare/Medicaid enrolled, SNAP + LIHEAP applied, primary care reconnected, home safety assessment scheduled.");
  bullet("90 Days (Resourced): grab bars and ramps installed, Harris County RIDES scheduled, in-home care visits underway.");
  bullet("6–12 Months (Connected): mail-order Rx delivery, weekly social touch-points, Senior Companion check-ins, caregiver respite.");
  bullet("Ongoing (Aging-in-Place): safely at home, integrated socially, financially protected, with dignity preserved.");

  heading("One Intake. One Day. Three Generations.");
  para(
    "The Hernandez household is a composite Bridge intake — illustrative of how a single connector activates the full ecosystem for one multigenerational family on a single day. The traditional safety net would route this household to three separate agencies, three separate intakes, and three separate case workers. Bridge does it on one couch in 90 minutes."
  );
  subheading("Maria (Mother, 34)");
  bullet("Lee College medical billing certificate enrollment.");
  bullet("Workforce Solutions childcare subsidy approved.");
  bullet("Texas Rent Relief application filed (back rent stabilized).");
  bullet("Medicaid + Marketplace ACA assignment.");
  subheading("Diego (Child, 7)");
  bullet("GCCISD Head Start / after-school placement.");
  bullet("Pediatric primary care via Harris Health.");
  bullet("Free dental + vision through CHIP.");
  bullet("Weekend literacy tutor through Lee College practicum.");
  subheading("Rosa (Grandmother, 68)");
  bullet("Medicare home health PT + aide visits.");
  bullet("Home modification: grab bars, ramps, lever handles.");
  bullet("SNAP + LIHEAP + Texas Over-65 tax freeze filed.");
  bullet("Harris County RIDES scheduled for monthly cardiology.");
  para(
    "ROI math, single intake: 1 connector vs. 3 agencies. 90-minute intake vs. 5–7 appointments over 6 weeks. Same-day service activation vs. 30–90 days. Coordination owned by the connector vs. coordination falling on the mother."
  );

  heading("The Missing Bridge");
  para(
    "Baytown has strong individual programs, but they are siloed. The Housing Authority's FSS program serves only existing voucher holders (waitlists are closed). Harris County housing stabilization is a crisis-intervention model — it activates only when an eviction notice is served. CK Family Services wraparound case management is restricted to Medicaid-funded behavioral health cases. None of them is a permanent, multi-year, cross-agency case manager for the general low- to moderate-income population. The Baytown Bridge fills that exact void."
  );

  heading("Founder Authority");
  para(
    "George Broughton III managed a 105-family service-connector caseload at The Woodlawn Organization / WCDC in Chicago from April 2001 to May 2007 — a federally-funded model linking public housing residents with employment, education, healthcare, and social services. He is Founder & CEO of Sterile Mate LLC, a Houston-based medical-supply company he built during the COVID-19 pandemic to serve the Texas Medical Center, and brings six years (2015–2021) of FAA-compliant Training & Development curriculum design at United Airlines. The Baytown Bridge is a localized adaptation of that proven model — sized for a mid-sized community across Harris and Chambers counties. Because the operational frame is battle-tested, the program skips the trial-and-error phase: caseload structure, intake matrices, and ROI tracking are turnkey from day one."
  );

  heading("The Tri-Portfolio Framework · 5-Year Multi-Agency Budget");
  para(
    "The City of Baytown acts as Master Administrator (the Hub) and awards three competitively-selected nonprofit service providers (the Spokes) five-year contracts with annual performance review. Each agency manages a 500-household portfolio. The Central Coordination Unit is staffed as a true executive unit — Architect, General Manager, Master Clinical Social Worker (LCSW-S), Central Field Manager, System Auditor, and Executive Assistant — supervising every agency. Year-1 recurring system investment: $4,134,560 across 1,500 households — $2,756 per household per year, or about $230 per month. With the Sterile Mate Developer Group's Year-1 program build added, the Year-1 all-in investment is $4,649,560. With 3% annual cost-of-living raises on all salaried positions and non-salary operating costs held flat, the 5-year recurring cumulative is $21,894,000 — or $22,916,500 all-in if the City exercises all three Developer Group option years."
  );

  subheading("Multi-Agency Operational Plan");
  bullet("Months 1–2: City Council resolution + pooled funding (CDBG, MDD, Harris County matches).");
  bullet("Months 3–4: RFP release and competitive selection of three service-provider agencies.");
  bullet("Month 5: Central software & protocol standardization (Apricot / ClientTrack, 45+ seats).");
  bullet("Month 6+: Frontline staffing (1 LCSW PM + 2 BS-SW Supervisors + 13 Service Connectors + 2 Intake/Data per agency) and intake of the first 1,500 active households.");

  subheading("Part A · Central Coordination Unit — $710,060 (Year 1)");
  bullet("Executive Program Director — 1.0 FTE — $135,000 — Permanent City of Baytown employee. Top of the Hub org chart. Owns annual review of the three Spoke agencies, RFP renewals, and the agency-independence transition once the launch-phase Developer Group rolls off.");
  bullet("General Manager · Hub Operations — 1.0 FTE — $105,000 — day-to-day Hub operations, RFP compliance, vendor management, finance & budget oversight, board liaison.");
  bullet("Master Clinical Social Worker (LCSW-S) — 1.0 FTE — $95,000 — Clinical Director with supervisory authority over the three agency LCSW Program Managers; sets clinical protocols and trauma-informed-care standards system-wide.");
  bullet("Central Field Manager · Quality & Compliance — 1.0 FTE — $72,000 — unannounced spot agency visits, fidelity-to-model audits, on-site training delivery, case-file sampling across all three Spokes.");
  bullet("System Auditor — 1.0 FTE — $68,000 — City compliance manager monitoring performance metrics, financial reporting, and RFP deliverable scorecards.");
  bullet("Executive Assistant — 1.0 FTE — $48,000 — calendar, board coordination, partner-relations support, document control, Council briefing prep.");
  bullet("Payroll Taxes & Benefits Load — 22% of City-employee salaries — $115,060.");
  bullet("Data System Provisioning (enterprise license, 45+ seats) — $32,000.");
  bullet("Independent Annual Evaluation (third-party academic / professional) — $25,000.");
  bullet("Indirect & Admin Cost (legal, marketing, general admin) — $15,000.");

  subheading("Part B · Per Agency Contract — $1,141,500 (Year 1, × 3 = $3,424,500)");
  bullet("Program Manager (LCSW Required) — 1.0 FTE — $75,000 — Licensed Clinical Social Worker, agency clinical lead, supervised by the Hub LCSW-S.");
  bullet("Track Supervisors (BS Social Work) — 2.0 FTE — $130,000 — 1 Family Track + 1 Senior Track @ $65,000 each.");
  bullet("Service Connectors (Case Managers) — 13.0 FTE @ $48,000 — $624,000 — case-management background preferred (not licensure-required); caseload cap 1:38.");
  bullet("Intake & Data Specialists — 2.0 FTE @ $38,000 — $76,000.");
  bullet("Payroll Taxes & Benefits Load — 22% of salaries — $199,100.");
  bullet("Localized Office Operations (rent, mileage, utilities, phones, supplies) — $37,400.");

  subheading("Part C · Sterile Mate Developer Group — $515,000 (Year 1)");
  para(
    "A separate, time-limited consulting engagement to develop curriculum, intake matrices, the data platform, and to train-the-trainer. Designed to make Baytown operationally independent of Sterile Mate by Year 3. Years 1 and 2 are firm; Years 3–5 are City options."
  );
  bullet("Principal Consultant & Program Creator — George Broughton III / Sterile Mate LLC — $135,000 — Founding Architect of the model; leads development, train-the-trainer, and the 2-year handoff.");
  bullet("LCSW · Clinical Curriculum Lead — sub-contracted Houston-area LCSW — $95,000 — trauma-informed care curriculum, clinical supervision protocols, intake matrix design.");
  bullet("Senior Living Social Worker — sub-contracted Texas-licensed senior-care SW — $85,000 — Senior Care Pipeline curriculum: aging-in-place, AAA navigation, in-home care coordination.");
  bullet("Employment Specialist — sub-contracted workforce-development expert — $75,000 — Trade Pipeline curriculum: Lee College certificate pathways, Workforce Solutions integration.");
  bullet("Continuing Education Specialist — sub-contracted adult-ed / training designer — $75,000 — builds the connector recertification pipeline and the train-the-trainer materials library.");
  bullet("Direct Expenses (curriculum materials, travel, train-the-trainer venue) — $50,000.");

  subheading("Developer Group · Multi-Year Schedule");
  bullet("Year 1 (Firm) — Develop · Build · Train — $515,000 — Months 1–6 program development, Months 7–12 train-the-trainer rollout to the Hub executive team and all three Spoke agencies.");
  bullet("Year 2 (Firm) — Stabilize · Audit · Hand off — $257,500 — 50% engagement; quarterly fidelity audits, connector recertification, agency-supervisor coaching, begins formal independence transition.");
  bullet("Year 3 (Option) — Supervised Independence — $125,000 — 25% engagement at the City's option; spot reviews, annual recertification refresh, problem-case consultation only.");
  bullet("Year 4 (Option) — Advisory Only — $75,000 — quarterly advisory retainer at the City's option; Baytown executive team owns operations.");
  bullet("Year 5 (Option) — Advisory Only — $50,000 — annual advisory check-in at the City's option; full Baytown independence achieved.");
  bullet("2-Year Firm Commitment: $772,500 · 5-Year Maximum (all options exercised): $1,022,500.");

  subheading("The Three Portfolios");
  bullet("Agency 1 · Public Housing Portfolio — 500 households embedded with Baytown Housing Authority FSS.");
  bullet("Agency 2 · Section 8 Voucher Portfolio — 500 voucher-holders in private-market units.");
  bullet("Agency 3 · General Community Portfolio — 500 LMI households not on federal housing assistance.");

  subheading("5-Year Forecast · 3% Annual Cost-of-Living Raise");
  bullet("Year 1: Hub $710,060 + 3 × $1,141,500 = $4,134,560 recurring · + $515,000 Dev Group = $4,649,560 all-in · $2,756/HH.");
  bullet("Year 2: Hub $729,202 + 3 × $1,174,623 = $4,253,071 recurring · + $257,500 Dev Group = $4,510,571 all-in · $2,835/HH.");
  bullet("Year 3: Hub $748,918 + 3 × $1,208,740 = $4,375,138 recurring · + $125,000 Dev Group = $4,500,138 all-in · $2,917/HH.");
  bullet("Year 4: Hub $769,226 + 3 × $1,243,880 = $4,500,866 recurring · + $75,000 Dev Group = $4,575,866 all-in · $3,001/HH.");
  bullet("Year 5: Hub $790,143 + 3 × $1,280,074 = $4,630,365 recurring · + $50,000 Dev Group = $4,680,365 all-in · $3,087/HH.");
  bullet("5-Year Recurring Cumulative: $21,894,000 · 5-Year All-In (with all Dev Group options): $22,916,500.");

  subheading("Governance & Conflict-of-Interest");
  para(
    "The Sterile Mate Developer Group engagement is awarded as a sole-source City professional-services contract to the program creator, with a written non-compete on all three Spoke RFPs for the full contract term plus a 12-month cool-down. The Principal Consultant serves the evaluation committee in an advisory, non-voting capacity. This structure — separating system design from agency operation — is the institutional safeguard that allows the City to engage the model's creator without compromising the integrity of the competitive RFP process."
  );

  subheading("The ROI · Cost-Avoidance Math");
  bullet("Bridge Tri-Portfolio System (Recurring) — $2,756 / household / year in Year 1 (~$230 / month). Holds under $3,100/HH even in Year 5.");
  bullet("Skilled nursing-home placement — $90,000+/yr (Bridge prevents this with home mods + Medicare home health).");
  bullet("Emergency shelter / coordinated entry — ~$35,000 / household (Bridge intervenes 6 weeks earlier).");
  bullet("Preventable senior EMS dispatch — $1,800 each (70% preventable with PCP continuity).");
  para(
    "Workforce generation: 100 Lee College graduates from Agency 3 alone, moving into living-wage industrial / medical / logistics careers, generate enough local spending and tax base to cover the operational cost of the entire system — with surplus. Cost avoidance: 500 seniors aging in place across all three portfolios dramatically cuts the City's EMS dispatch burden and avoids public-homelessness crises that already cost Baytown more than $2,756 per household, every year, with no exit strategy."
  );

  // ---------- Program Goals · 5-Year Outcome Forecast ----------
  heading("Program Goals · 5-Year Outcome Forecast");
  para(
    "The dollar-side commitments are paired with these program-side commitments. Year-1 ratios are derived from the published research base and Bridge's Chicago / WCDC operating history (105-family caseload × 6 years). Year-by-year targets are baked into each Spoke's annual performance scorecard."
  );
  subheading("Year-by-Year Targets");
  bullet("Year 1 — 1,500 HH served · 240 Lee College enrollments · 0 graduates yet (12–24 mo certificates) · 180 home modifications · 425 Medicare enrollments · $2.4M benefits recovered · 145 evictions prevented.");
  bullet("Year 2 — 1,500 HH served · 320 enrollments · 95 graduates · 220 home mods · 460 Medicare enrollments · $2.75M benefits recovered · 175 evictions prevented.");
  bullet("Year 3 — 1,500 HH served · 360 enrollments · 165 graduates · 200 home mods · 475 Medicare enrollments · $2.9M benefits recovered · 190 evictions prevented.");
  bullet("Year 4 — 1,500 HH served · 380 enrollments · 195 graduates · 195 home mods · 480 Medicare enrollments · $3.0M benefits recovered · 200 evictions prevented.");
  bullet("Year 5 — 1,500 HH served · 400 enrollments · 215 graduates · 190 home mods · 485 Medicare enrollments · $3.1M benefits recovered · 210 evictions prevented.");
  bullet("5-Year Totals — 7,500 cumulative household service-years · 1,700 Lee College enrollments · 670 graduates · 985 home modifications · 2,325 Medicare enrollments · $14.15M benefits recovered · 920 evictions prevented.");

  subheading("Non-Negotiable Outcome Benchmarks");
  bullet("Caseload Cap (Service Connector : Households) — 1 : 38 — federal high-touch case-management standard.");
  bullet("Intake → First Service — same day — single-intake model, multi-generational household assessed and enrolled in one 90-minute visit.");
  bullet("Service Continuity — 12–24 months minimum — Bridge is a stabilization program, not a crisis hotline.");
  bullet("Annual Benefits Recovered / Senior — $3,000–$6,000 — SNAP, LIHEAP, Texas Over-65 freeze, Medicare Savings, Extra Help, VA screening.");
  bullet("Preventable Senior EMS Reduction — ≥ 40% over 24 months — PCP continuity + chronic-disease management installed within 30 days of intake.");
  bullet("Lee College Completion Rate — ≥ 60% of enrolled cohort — childcare subsidy + connector retention support closes the dropout gap.");
  bullet("Independent Annual Evaluation — Year-end published — third-party academic or professional evaluator; public-record accountability.");
  bullet("Agency Performance Review — Annual per Spoke — RFP renewal contingent on benchmark performance; non-performing agency replaced via re-bid.");

  // ---------- Implementation Strategy ----------
  heading("Implementation Strategy · 12-Month Launch Arc");
  para(
    "Five overlapping phases executed inside Year 1, driven by the Sterile Mate Developer Group's firm Develop · Build · Train engagement. Every phase ends with a signed deliverable; every quarter ends with a Go / No-Go gate logged to City Council."
  );

  subheading("Phase 1 · Months 1–6 · Program Development");
  para(
    "Lead: Sterile Mate Developer Group · Cost: $515,000 (Year-1 Developer Group). The Architect and the four sub-contracted specialists build the operating system: standardized intake matrix, Senior + Family + Trade pipeline curricula, the data platform (Apricot or ClientTrack) configured for cross-Spoke reporting, and the KPI dashboards the City will use for annual agency review."
  );
  bullet("Standardized intake matrix (Senior + Family + Multigenerational).");
  bullet("Trauma-informed care clinical supervision protocols.");
  bullet("Senior Care Pipeline curriculum (aging-in-place, AAA, in-home care).");
  bullet("Trade Pipeline curriculum (Lee College + Workforce Solutions).");
  bullet("Apricot / ClientTrack platform deployed (45+ seats provisioned).");
  bullet("Agency performance scorecard + KPI dashboards live in the platform.");

  subheading("Phase 2 · Months 4–7 · Train-the-Trainer · Hub Executive Team");
  para(
    "Lead: Sterile Mate Developer Group → City of Baytown · Sourced from: Year-1 Developer Group budget. Sterile Mate trains the City's 6-FTE Central Coordination Unit (EPD, GM, Master LCSW-S, Central Field Manager, System Auditor, Executive Assistant) to own clinical, compliance, and field-audit authority for the entire Tri-Portfolio system. A 12-week intensive at Sterile Mate's Houston facility plus on-site at City Hall, ending with each Hub member graduating with a written competency portfolio."
  );
  bullet("Fidelity-to-model audit playbook (Central Field Manager).");
  bullet("RFP compliance scorecard + remediation protocol (System Auditor).");
  bullet("Clinical supervision cadence + crisis-escalation tree (Master LCSW-S).");
  bullet("Vendor / partner SLA framework (General Manager).");
  bullet("Council briefing & board package templates (Executive Assistant).");
  bullet("Signed Hub Team Competency Portfolios (6 of 6).");

  subheading("Phase 3 · Months 5–8 · Agency Onboarding & Training");
  para(
    "Lead: Sterile Mate + Hub LCSW-S + Field Manager · Sourced from: Year-1 Developer Group budget. Once the RFP is awarded (~Month 4), Sterile Mate and the trained Hub jointly run training cohorts for the three Spoke agencies — 18 FTEs per agency. Eight-week curriculum with live shadowing on test intakes, a certification exam, and fidelity-to-model sign-off before any Spoke is cleared to accept its first household."
  );
  bullet("Service Connector certification (54 of 54 across 3 agencies).");
  bullet("LCSW Program Manager clinical supervision pact (3 of 3).");
  bullet("Intake & Data Specialist platform certification (6 of 6).");
  bullet("Track Supervisor (BS-SW) field shadow sign-off (6 of 6).");
  bullet("Each agency's executive director countersigns fidelity-to-model agreement.");

  subheading("Phase 4 · Month 6 · Soft Launch · 150-Household Intake Pilot");
  para(
    "Lead: All 3 Spokes + Hub + Sterile Mate (observer) · Sourced from: Year-1 Operating Budget. Each Spoke takes 50 households (10% of its annual portfolio) through a 4-week controlled pilot to stress-test the platform, the intake protocol, and the handoff between Spokes and the partner network (Lee College, Harris Health, Housing Authority, AAA, Workforce Solutions). Weekly executive standup; daily platform feedback loop; documented punch-list before full launch is cleared."
  );
  bullet("150 active households fully enrolled (50 per Spoke).");
  bullet("Platform punch-list resolved (sub-72-hour ticket SLA).");
  bullet("Partner-network handoff audit (all 9 referral partners).");
  bullet("Pilot retrospective signed by Hub + 3 Spoke EDs.");
  bullet("Go / No-Go decision logged with City Council.");

  subheading("Phase 5 · Months 7–12 · Full Launch · 1,500 Households");
  para(
    "Lead: 3 Spokes + Hub (Sterile Mate transitions to audit role) · Sourced from: Year-1 Operating Budget. Full intake of the remaining 1,350 households via the partner network — direct data integration with the Baytown Housing Authority for proactive recruitment, GCCISD family liaisons, Harris Health primary-care intake, AAA senior referrals, and a city-led public marketing campaign. The independent evaluator begins data collection. By Month 12, the program publishes its first annual public report and the City conducts its first formal annual review of all three Spokes."
  );
  bullet("1,500 active households (500 per Spoke, full caseload).");
  bullet("Caseload cap held at 1:38 across all 3 agencies.");
  bullet("Independent annual evaluation published.");
  bullet("Annual agency review (3 of 3) completed by City Council.");
  bullet("Year-2 contract renewals issued · Sterile Mate engagement steps down to 50%.");

  subheading("City Council Go / No-Go Gates");
  bullet("Gate 1 · End of Month 6 — Soft-Launch Go / No-Go — platform live · 150-HH pilot complete · 95%+ certification pass rate · zero outstanding fidelity-to-model exceptions.");
  bullet("Gate 2 · End of Month 9 — Mid-Year Performance Review — 750-household milestone hit · caseload ≤ 1:38 across all 3 agencies · weekly KPI dashboard reviewed by Hub LCSW-S and System Auditor.");
  bullet("Gate 3 · End of Month 12 — Year-1 Annual Review · 3 Spokes — 1,500-HH milestone · independent evaluation published · agency-specific performance scorecards reviewed by City Council · Year-2 contract renewals authorized.");

  heading("Municipal Partnership & Ask");
  para(
    "Baytown is a direct HUD entitlement jurisdiction with an active CDBG funding cycle that explicitly seeks non-profit and public service partners serving low- to moderate-income residents. The Baytown Bridge requests a CDBG public-service sub-recipient award to fund initial case worker hires, intake infrastructure, and a coordinated referral protocol with Lee College, the Baytown Housing Authority, and Harris Health. We multiply the city's existing investments rather than competing with them."
  );

  heading("Contact");
  para("George Broughton — Program Creator & CEO, Sterile Mate LLC");
  para("Email: george.broughton@sterilemate.com");
  para("Schedule a 30-minute municipal briefing at baytownbridge.org/contact");

  drawFooter();
  doc.save("baytown-bridge-proposal.pdf");
}
